import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const SubscriptionManage = () => {
  const { user } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState({
    name: '',
    price: '',
    interval: 'monthly',
    description: '',
  });
  const [editPlan, setEditPlan] = useState(null);
  const [deletePlanId, setDeletePlanId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Format interval for display
  const formatInterval = (interval) => {
    switch (interval) {
      case 'monthly':
        return 'Monthly';
      case 'yearly':
        return 'Yearly';
      case '2years':
        return '2 Years';
      case '3years':
        return '3 Years'
      default:
        return interval.charAt(0).toUpperCase() + interval.slice(1);
    }
  };

  // Validate plan object
  const isValidPlan = (plan) => {
    return (
      plan &&
      typeof plan === 'object' &&
      typeof plan._id === 'string' &&
      typeof plan.name === 'string' &&
      typeof plan.price === 'number' &&
      !isNaN(plan.price) &&
      plan.price >= 0 &&
      ['monthly', 'yearly', '2years', '3years'].includes(plan.interval) &&
      typeof plan.description === 'string'
    );
  };

  // Fetch existing plans with validation
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await apiService.get('/stripe/plans');
        if (!Array.isArray(response)) {
          throw new Error('Invalid response: Expected an array of plans');
        }
        const validPlans = response.filter(isValidPlan);
        setPlans(validPlans);
        if (validPlans.length !== response.length) {
          setError('Some plans were invalid and filtered out.');
        } else {
          setError('');
        }
      } catch (err) {
        setError('Failed to fetch plans. Please try again.');
        console.error('Fetch plans error:', err);
      }
    };

    if (user?.role === 'admin') {
      fetchPlans();
    }
  }, [user]);

  // Toast notifications
  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      toast.success(success, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [success]);

  // Handle form input changes
  const handleInputChange = (e, setPlan) => {
    const { name, value } = e.target;
    setPlan((prev) => ({ ...prev, [name]: value }));
  };

  // Handle CKEditor changes
  const handleEditorChange = (event, editor, setPlan) => {
    const data = editor.getData();
    setPlan((prev) => ({ ...prev, description: data }));
  };

  // Create new plan
  const handleCreatePlan = async (e) => {
    e.preventDefault();

    const trimmedName = newPlan.name.trim();
    const priceNum = parseFloat(newPlan.price);

    if (!trimmedName || isNaN(priceNum) || priceNum <= 0 || !newPlan.description.trim()) {
      setError('Please provide a valid plan name, a positive price, and a description.');
      return;
    }

    try {
      const response = await apiService.post('/stripe/create-plan', {
        name: trimmedName,
        price: priceNum,
        interval: newPlan.interval,
        description: newPlan.description,
      });

      if (!response.data || !isValidPlan(response.data)) {
        throw new Error('Invalid response: Plan data is missing or incomplete');
      }

      setPlans([...plans, response.data]);
      setSuccess('Plan created successfully!');
      setNewPlan({ name: '', price: '', interval: 'monthly', description: '' });
      setIsAddModalOpen(false);
      setError('');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to create plan';
      setError(errorMsg);
      console.error('Error creating plan:', err);
    }
  };

  // Update existing plan
  const handleUpdatePlan = async (e) => {
    e.preventDefault();

    if (!editPlan) return;

    const trimmedName = editPlan.name.trim();
    const priceNum = parseFloat(editPlan.price);

    if (!trimmedName || isNaN(priceNum) || priceNum <= 0 || !editPlan.description.trim()) {
      setError('Please provide a valid plan name, a positive price, and a description.');
      return;
    }

    try {
      const response = await apiService.put(`/stripe/plans/${editPlan._id}`, {
        name: trimmedName,
        price: priceNum,
        interval: editPlan.interval,
        description: editPlan.description,
      });

      if (!response.data || !isValidPlan(response.data)) {
        throw new Error('Invalid response: Updated plan data is missing or incomplete');
      }

      setPlans(plans.map(p => (p._id === editPlan._id ? response.data : p)));
      setSuccess('Plan updated successfully!');
      setEditPlan(null);
      setIsEditModalOpen(false);
      setError('');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update plan';
      setError(errorMsg);
      console.error('Error updating plan:', err);
    }
  };

  // Delete plan
  const handleDeletePlan = async () => {
    if (!deletePlanId) {
      setError('No plan selected for deletion.');
      return;
    }

    try {
      await apiService.delete(`/stripe/plans/${deletePlanId}`);
      setPlans(plans.filter(plan => plan._id !== deletePlanId));
      setSuccess('Plan deleted successfully!');
      setDeletePlanId(null);
      setIsDeleteModalOpen(false);
      setError('');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete plan';
      setError(errorMsg);
      console.error('Error deleting plan:', err);
    }
  };

  // Open edit modal
  const openEditModal = (plan) => {
    if (!isValidPlan(plan)) {
      setError('Cannot edit invalid plan.');
      return;
    }
    setEditPlan({
      ...plan,
      price: plan.price.toString(),
    });
    setIsEditModalOpen(true);
  };

  // Open delete confirmation
  const openDeleteModal = (planId) => {
    if (!planId || typeof planId !== 'string') {
      setError('Invalid plan ID for deletion.');
      return;
    }
    setDeletePlanId(planId);
    setIsDeleteModalOpen(true);
  };

  // Non-admin access
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-100 py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-red-600 text-center text-lg">Access denied. Admin only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 sm:py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-6 sm:mb-8">
          Manage Subscriptions
        </h1>

        <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 max-w-5xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Subscription Plans</h2>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition duration-200 font-medium text-sm sm:text-base"
            >
              Add New Plan
            </button>
          </div>

          {plans.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No subscription plans available yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-gray-50 rounded-lg border border-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left p-4 text-sm font-semibold text-gray-700">ID</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-700">Price (£)</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-700">Interval</th>
                    <th className="text-left p-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => (
                    <tr key={plan._id} className="border-t border-gray-200 hover:bg-gray-50">
                      <td className="p-4 text-sm text-gray-600 font-mono">{plan._id}</td>
                      <td className="p-4 text-sm text-gray-800 font-medium">{plan.name}</td>
                      <td className="p-4 text-sm text-gray-600">£{plan.price.toFixed(2)}</td>
                      <td className="p-4 text-sm text-gray-600">{formatInterval(plan.interval)}</td>
                      <td className="p-4 text-sm">
                        <button
                          onClick={() => openEditModal(plan)}
                          className="text-indigo-600 hover:text-indigo-800 font-medium mr-5"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDeleteModal(plan._id)}
                          className="text-red-600 hover:text-red-800 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add Plan Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Create New Subscription Plan</h2>

              <form onSubmit={handleCreatePlan} className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Name</label>
                  <input
                    type="text"
                    name="name"
                    value={newPlan.name}
                    onChange={(e) => handleInputChange(e, setNewPlan)}
                    placeholder="e.g. Premium Monthly"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Price (GBP)</label>
                  <input
                    type="number"
                    name="price"
                    value={newPlan.price}
                    onChange={(e) => handleInputChange(e, setNewPlan)}
                    placeholder="e.g. 29.99"
                    step="0.01"
                    min="0.01"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Billing Interval</label>
                  <select
                    name="interval"
                    value={newPlan.interval}
                    onChange={(e) => handleInputChange(e, setNewPlan)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="monthly">1 Month</option>
                    <option value="yearly">1 Year</option>
                    <option value="2years">2 Years</option>
                    <option value="3years">3 Years</option>
                    
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <CKEditor
                      editor={ClassicEditor}
                      data={newPlan.description}
                      onChange={(event, editor) => handleEditorChange(event, editor, setNewPlan)}
                      config={{
                        toolbar: [
                          'heading', '|',
                          'bold', 'italic', 'underline', 'link', '|',
                          'bulletedList', 'numberedList', '|',
                          'blockQuote', '|',
                          'undo', 'redo'
                        ],
                        placeholder: 'Describe what this plan includes...',
                      }}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 lg:col-span-2 mt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-3 px-6 rounded-lg hover:bg-indigo-700 transition font-medium"
                  >
                    Create Plan
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsAddModalOpen(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Plan Modal */}
        {isEditModalOpen && editPlan && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Edit Subscription Plan</h2>

              <form onSubmit={handleUpdatePlan} className="space-y-6 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editPlan.name || ''}
                    onChange={(e) => handleInputChange(e, setEditPlan)}
                    placeholder="e.g. Premium Monthly"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Price (GBP)</label>
                  <input
                    type="number"
                    name="price"
                    value={editPlan.price || ''}
                    onChange={(e) => handleInputChange(e, setEditPlan)}
                    placeholder="e.g. 29.99"
                    step="0.01"
                    min="0.01"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Billing Interval</label>
                  <select
                    name="interval"
                    value={editPlan.interval || 'monthly'}
                    onChange={(e) => handleInputChange(e, setEditPlan)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="monthly">1 Month</option>
                    <option value="yearly">1 Year</option>
                    <option value="2years">2 Years</option>
                    <option value="3years">3 Years</option>
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <CKEditor
                      editor={ClassicEditor}
                      data={editPlan.description || ''}
                      onChange={(event, editor) => handleEditorChange(event, editor, setEditPlan)}
                      config={{
                        toolbar: [
                          'heading', '|',
                          'bold', 'italic', 'underline', 'link', '|',
                          'bulletedList', 'numberedList', '|',
                          'blockQuote', '|',
                          'undo', 'redo'
                        ],
                        placeholder: 'Describe what this plan includes...',
                      }}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 lg:col-span-2 mt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-3 px-6 rounded-lg hover:bg-indigo-700 transition font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Confirm Deletion</h2>
              <p className="text-gray-600 mb-8">
                Are you sure you want to delete this subscription plan? This action cannot be undone.
              </p>

              <div className="flex gap-4">
                <button
                  onClick={handleDeletePlan}
                  className="flex-1 bg-red-600 text-white py-3 px-6 rounded-lg hover:bg-red-700 transition font-medium"
                >
                  Delete Plan
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-6 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </div>
  );
};

export default SubscriptionManage;